import { useState } from 'react';
import { Col, FormGroup, FormLabel, Row, Button } from 'react-bootstrap';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import NewPage from '../../../layouts/page/NewPage';
import InputText from '../../../components/Forms/Element/InputText';
import AsteriskRequired from '../../../components/Common/AsteriskRequired';
import StatusCheckBox from '../../../components/Common/StatusCheckbox';
import MenuConfig from '../../../../constants/MenuConfig';
import CompanyApiService from '../../../../services/superadmin/CompanyApiService';
import { errorAlert } from '../../../components/alert/defaultAlert';
import FormSection from '../../../components/Common/Form/FormSection';
import './CompanyApiNew.css';

const CopyField = ({ label, caption, value, copyLabel, copiedLabel }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
        await navigator.clipboard.writeText(value);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <div className="secret-result__field">
            <div className="secret-result__field-header">
                <div>
                    <FormLabel>{label}</FormLabel>
                    <div className="secret-result__field-caption">{caption}</div>
                </div>
                <Button
                    type="button"
                    variant={copied ? 'success' : 'secondary'}
                    onClick={handleCopy}
                    className="secret-result__copy"
                >
                    <i className={`fa-solid ${copied ? 'fa-check' : 'fa-copy'}`} />
                    <span className="ms-2">{copied ? copiedLabel : copyLabel}</span>
                </Button>
            </div>

            <div className="secret-result__value">{value}</div>
        </div>
    );
};

const CompanyApiNew = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [generatedCredentials, setGeneratedCredentials] = useState(null);
    const [active, setActive] = useState(true);

    const resetGeneratedCredentials = () => {
        setGeneratedCredentials(null);
    };

    const formik = useFormik({
        initialValues: {
            description: ''
        },
        validationSchema: Yup.object().shape({
            description: Yup.string()
                .trim()
                .min(3, t('company_api.description_min'))
                .required(t('company_api.description_required'))
        }),
        onSubmit: async (values, { resetForm, setSubmitting }) => {
            try {
                const response = await CompanyApiService.create({
                    description: values.description.trim(),
                    active
                });

                setGeneratedCredentials({
                    clientId: response.data.clientId,
                    clientSecret: response.data.clientSecret,
                });
                resetForm();
                setActive(true);
            } catch (error) {
                errorAlert(t('company_api.create_error'));
            } finally {
                setSubmitting(false);
            }
        }
    });

    const handleSubmit = (e) => {
        e.preventDefault();
        formik.handleSubmit();
    };

    return (
        <NewPage
            motherMenu={t('administration')}
            childMenu={t('company_api.credentials')}
            activeMenu={t('new')}
            child={
                <div className="form-layout form-layout--spaced">
                    {generatedCredentials ? (
                        <FormSection className="secret-result" icon="fa-solid fa-shield-halved">
                                    <div className="secret-result__title-wrap">
                                        <div className="secret-result__icon">
                                            <i className="fa-solid fa-shield-halved" />
                                        </div>
                                        <div>
                                            <h4 className="mb-1 secret-result__title">{t('company_api.created_title')}</h4>
                                            <p className="mb-0">{t('company_api.created_subtitle')}</p>
                                        </div>
                                    </div>

                            <div className="secret-result__layout">
                                <div className="secret-result__main">
                                    <div className="secret-result__stack">
                                        <CopyField
                                            label={t('company_api.client_id')}
                                            caption={t('company_api.client_id_caption')}
                                            value={generatedCredentials.clientId}
                                            copyLabel={t('copy.copy')}
                                            copiedLabel={t('copy.copied')}
                                        />
                                        <CopyField
                                            label={t('company_api.client_secret')}
                                            caption={t('company_api.client_secret_caption')}
                                            value={generatedCredentials.clientSecret}
                                            copyLabel={t('copy.copy')}
                                            copiedLabel={t('copy.copied')}
                                        />
                                    </div>
                                </div>

                                <aside className="secret-result__aside">
                                    <div className="secret-result__banner">
                                        <i className="fa-solid fa-triangle-exclamation" />
                                        <div>
                                            <strong>{t('warning')}</strong>
                                            <span>{t('company_api.secret_one_time_warning')}</span>
                                        </div>
                                    </div>

                                    <div className="secret-result__hint-card">
                                        <h6 className="mb-2">{t('company_api.best_practices_title')}</h6>
                                        <p className="mb-2">{t('company_api.best_practices_line_one')}</p>
                                        <p className="mb-0">{t('company_api.best_practices_line_two')}</p>
                                    </div>
                                </aside>
                            </div>
                        </FormSection>
                    ) : null}

                    {!generatedCredentials ? (
                        <>
                            <FormSection title={t('company_api.new_title')} description={t('company_api.new_hint')} icon="fa-solid fa-key">

                                <Row className="g-3">
                                    <Col md={6}>
                                        <FormGroup>
                                            <FormLabel>{t('description')} <AsteriskRequired /></FormLabel>
                                            <InputText
                                                name="description"
                                                placeholder={t('company_api.description_placeholder')}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                value={formik.values.description}
                                                error={formik.touched.description ? formik.errors.description : ''}
                                                maxLength="150"
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>
                            </FormSection>

                            <FormSection title={t('status')} description={t('company_api.status_hint')} icon="fa-solid fa-toggle-on" compact>

                                <Row>
                                    <Col md={12}>
                                        <FormGroup className="mb-0">
                                            <StatusCheckBox
                                                name="active"
                                                defaultChecked={active}
                                                checked={active}
                                                onClick={() => setActive(!active)}
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>
                            </FormSection>
                        </>
                    ) : null}
                </div>
            }
            isSubmitting={formik.isSubmitting}
            handleSubmit={handleSubmit}
            cancelRoute={MenuConfig.superadmin.company_api.route}
            hideSaveButton={!!generatedCredentials}
            hideCancelButton={!!generatedCredentials}
            customButtons={generatedCredentials ? (
                <>
                    <Button
                        type="button"
                        variant="light"
                        className="me-2"
                        onClick={resetGeneratedCredentials}
                    >
                        {t('company_api.new_credential')}
                    </Button>
                    <Button
                        type="button"
                        variant="primary"
                        onClick={() => navigate(MenuConfig.superadmin.company_api.route)}
                    >
                        {t('company_api.back_to_list')}
                    </Button>
                </>
            ) : null}
        />
    );
};

export default CompanyApiNew;
